/**
 * Upstream Issues — English corrections + translation corrections
 * for upstream Plone platforms (plone, volto, volto_slate, volto_vlt).
 *
 * Three tabs:
 * 1. English Corrections — .pot source text improvements
 * 2. Translation Corrections — .po translation fixes
 * 3. Broken Placeholders — missing/malformed tokens (existing)
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

const platformColors = {
  nick: '#00b5ad', volto: '#2185d0', volto_slate: '#6435c9',
  volto_vlt: '#f2711c', plone: '#21ba45',
};

const PlatformLabel = ({ platform }) => (
  <span style={{
    display: 'inline-block', padding: '0.15em 0.5em', borderRadius: 3,
    color: 'white', fontSize: '0.8em', fontWeight: 600,
    background: platformColors[platform] || '#999',
  }}>{platform}</span>
);

// ============================================================
// Tab: English Corrections
// ============================================================
const EnglishTab = ({ summary }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [platforms, setPlatforms] = useState([]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ tab: 'english' });
    if (platform) params.append('platform', platform);
    if (search) params.append('search', search);
    fetchApi(`${getApiPath()}/@polyglot/qc/upstream-issues?${params}`)
      .then((data) => { setItems(data.items || []); setPlatforms(data.platforms || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [platform, search]);

  if (loading) return <p style={{ textAlign: 'center', padding: '2em', color: '#999' }}>Loading...</p>;

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.8em', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1em', padding: '0.8em 1em', background: '#f9f9f9', borderRadius: 4, border: '1px solid #eee' }}>
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} style={{ padding: '0.5em', border: '1px solid #ccc', borderRadius: 4 }}>
          <option value="">All platforms</option>
          {platforms.map((p) => <option key={p.platform} value={p.platform}>{p.platform} ({p.cnt})</option>)}
        </select>
        <input type="text" placeholder="Search..." value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
          onBlur={() => setSearch(searchInput)}
          style={{ padding: '0.5em', border: '1px solid #ccc', borderRadius: 4, minWidth: 180 }}
        />
        <span style={{ marginLeft: 'auto', color: '#999', fontSize: '0.9em' }}>{items.length} corrections</span>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.93em' }}>
        <thead>
          <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
            <th style={{ padding: '0.7em 0.8em', textAlign: 'left', width: '70px' }}>Platform</th>
            <th style={{ padding: '0.7em 0.8em', textAlign: 'left' }}>Original English</th>
            <th style={{ padding: '0.7em 0.8em', textAlign: 'left' }}>Corrected English</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <React.Fragment key={`${item.message_id}-${item.platform}`}>
              <tr onClick={() => setExpandedId(expandedId === item.message_id ? null : item.message_id)}
                style={{ cursor: 'pointer', borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '0.6em 0.8em' }}><PlatformLabel platform={item.platform} /></td>
                <td style={{ padding: '0.6em 0.8em', background: '#fff8f8', whiteSpace: 'pre-wrap' }}>{item.original_default}</td>
                <td style={{ padding: '0.6em 0.8em', background: '#f8fff8', whiteSpace: 'pre-wrap', fontWeight: 600 }}>{item.corrected_default}</td>
              </tr>
              {expandedId === item.message_id && (
                <tr><td colSpan="3" style={{ padding: '1em', background: '#f9f9f9' }}>
                  <p><strong>msgid:</strong> <code style={{ fontSize: '0.85em', background: '#f5f5f5', padding: '0.1em 0.3em', borderRadius: 2, wordBreak: 'break-all' }}>{item.msgid}</code></p>
                  <p><strong>Platform:</strong> {item.platform} | <strong>Domain:</strong> {item.domain} | <strong>Source:</strong> {item.has_default_comment ? '#. Default' : 'msgid'}</p>
                  <div style={{ display: 'flex', gap: '1em', marginTop: '0.5em' }}>
                    <div style={{ flex: 1 }}>
                      <p><strong>Original:</strong></p>
                      <div style={{ padding: '0.6em', borderRadius: 4, whiteSpace: 'pre-wrap', background: '#fff0f0', border: '1px solid #fdd', fontFamily: 'monospace', fontSize: '0.9em' }}>{item.original_default || '(none)'}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <p><strong>Corrected:</strong></p>
                      <div style={{ padding: '0.6em', borderRadius: 4, whiteSpace: 'pre-wrap', background: '#f0fff0', border: '1px solid #dfd', fontFamily: 'monospace', fontSize: '0.9em' }}>{item.corrected_default}</div>
                    </div>
                  </div>
                </td></tr>
              )}
            </React.Fragment>
          ))}
          {items.length === 0 && (
            <tr><td colSpan="3" style={{ textAlign: 'center', padding: '2em', color: '#999' }}>No corrections match the current filters.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================
// Tab: Translation Corrections
// ============================================================
const TranslationsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState('');
  const [language, setLanguage] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expandedIdx, setExpandedIdx] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ tab: 'translations' });
    if (platform) params.append('platform', platform);
    if (language) params.append('language', language);
    if (search) params.append('search', search);
    fetchApi(`${getApiPath()}/@polyglot/qc/upstream-issues?${params}`)
      .then((data) => setItems(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [platform, language, search]);

  if (loading) return <p style={{ textAlign: 'center', padding: '2em', color: '#999' }}>Loading...</p>;

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.8em', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1em', padding: '0.8em 1em', background: '#f9f9f9', borderRadius: 4, border: '1px solid #eee' }}>
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} style={{ padding: '0.5em', border: '1px solid #ccc', borderRadius: 4 }}>
          <option value="">All platforms</option>
          <option value="plone">plone</option>
          <option value="volto">volto</option>
          <option value="volto_slate">volto_slate</option>
          <option value="volto_vlt">volto_vlt</option>
        </select>
        <input type="text" placeholder="Language code (e.g. de)..." value={language}
          onChange={(e) => setLanguage(e.target.value.trim())}
          style={{ padding: '0.5em', border: '1px solid #ccc', borderRadius: 4, width: 160 }}
        />
        <input type="text" placeholder="Search..." value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
          onBlur={() => setSearch(searchInput)}
          style={{ padding: '0.5em', border: '1px solid #ccc', borderRadius: 4, minWidth: 180 }}
        />
        <span style={{ marginLeft: 'auto', color: '#999', fontSize: '0.9em' }}>{items.length} corrections (max 2000)</span>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.93em' }}>
        <thead>
          <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
            <th style={{ padding: '0.7em 0.8em', textAlign: 'left', width: '70px' }}>Platform</th>
            <th style={{ padding: '0.7em 0.8em', textAlign: 'left', width: '40px' }}>Lang</th>
            <th style={{ padding: '0.7em 0.8em', textAlign: 'left', width: '25%' }}>English</th>
            <th style={{ padding: '0.7em 0.8em', textAlign: 'left' }}>Original .po</th>
            <th style={{ padding: '0.7em 0.8em', textAlign: 'left' }}>Our Correction</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <React.Fragment key={`${item.message_id}-${item.language}-${idx}`}>
              <tr onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                style={{ cursor: 'pointer', borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '0.6em 0.8em' }}><PlatformLabel platform={item.platform} /></td>
                <td style={{ padding: '0.6em 0.8em', fontWeight: 600 }}>{item.language}</td>
                <td style={{ padding: '0.6em 0.8em', fontSize: '0.9em', whiteSpace: 'pre-wrap' }}>{item.english_source}</td>
                <td style={{ padding: '0.6em 0.8em', background: '#fff8f8', whiteSpace: 'pre-wrap' }}>{item.old_value}</td>
                <td style={{ padding: '0.6em 0.8em', background: '#f8fff8', whiteSpace: 'pre-wrap', fontWeight: 600 }}>{item.new_value}</td>
              </tr>
              {expandedIdx === idx && (
                <tr><td colSpan="5" style={{ padding: '1em', background: '#f9f9f9' }}>
                  <p><strong>Message ID:</strong> <Link to={`/polyglot/messages/${item.message_id}`}>{item.message_id}</Link> | <strong>Type:</strong> {item.correction_type} | <strong>Applied:</strong> {new Date(item.applied_at).toLocaleDateString()}</p>
                  <div style={{ display: 'flex', gap: '1em', marginTop: '0.5em' }}>
                    <div style={{ flex: 1 }}>
                      <p><strong>Original .po translation:</strong></p>
                      <div style={{ padding: '0.6em', borderRadius: 4, whiteSpace: 'pre-wrap', background: '#fff0f0', border: '1px solid #fdd', fontFamily: 'monospace', fontSize: '0.9em', wordBreak: 'break-word' }}>{item.old_value}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <p><strong>Our corrected translation:</strong></p>
                      <div style={{ padding: '0.6em', borderRadius: 4, whiteSpace: 'pre-wrap', background: '#f0fff0', border: '1px solid #dfd', fontFamily: 'monospace', fontSize: '0.9em', wordBreak: 'break-word' }}>{item.new_value}</div>
                    </div>
                  </div>
                </td></tr>
              )}
            </React.Fragment>
          ))}
          {items.length === 0 && (
            <tr><td colSpan="5" style={{ textAlign: 'center', padding: '2em', color: '#999' }}>
              {!language ? 'Enter a language code to see translation corrections.' : 'No corrections match the current filters.'}
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================
// Tab: Broken Placeholders (existing logic)
// ============================================================
const PlaceholdersTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApi(`${getApiPath()}/@polyglot/qc/broken-placeholders`)
      .then((json) => setData(json))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p style={{ textAlign: 'center', padding: '2em', color: '#999' }}>Loading...</p>;

  const { by_language = {}, items_total = 0 } = data || {};
  const sortedLangs = Object.keys(by_language).sort();

  if (items_total === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3em', color: '#21ba45' }}>
        <p style={{ fontSize: '1.2em', fontWeight: 600 }}>All clear — no broken placeholders found.</p>
        <p style={{ color: '#999' }}>Every placeholder token in every translation matches the English source.</p>
      </div>
    );
  }

  return (
    <div>
      {sortedLangs.map((lang) => (
        <div key={lang} style={{ marginBottom: '1.5em' }}>
          <h3>{lang} <span style={{ background: '#f2711c', color: 'white', padding: '0.2em 0.6em', borderRadius: 12, fontSize: '0.7em', marginLeft: '0.5em' }}>{by_language[lang].length}</span></h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '0.5em', textAlign: 'left', width: '60px' }}>ID</th>
                <th style={{ padding: '0.5em', textAlign: 'left', width: '70px' }}>Platform</th>
                <th style={{ padding: '0.5em', textAlign: 'left' }}>English Source</th>
                <th style={{ padding: '0.5em', textAlign: 'left' }}>Translation</th>
              </tr>
            </thead>
            <tbody>
              {by_language[lang].map((item) => (
                <tr key={`${item.message_id}-${item.platform}`} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.5em' }}><Link to={`/polyglot/messages/${item.message_id}`}>{item.message_id}</Link></td>
                  <td style={{ padding: '0.5em' }}><PlatformLabel platform={item.platform} /></td>
                  <td style={{ padding: '0.5em', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.english_source}</td>
                  <td style={{ padding: '0.5em', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.msgstr}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// Main component
// ============================================================
const UpstreamIssues = () => {
  const [activeTab, setActiveTab] = useState('english');
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetchApi(`${getApiPath()}/@polyglot/qc/upstream-issues?tab=english`)
      .then((data) => setSummary(data.summary))
      .catch(() => {});
  }, []);

  const tabs = [
    { key: 'english', label: `English Corrections${summary ? ` (${summary.english_corrections})` : ''}` },
    { key: 'translations', label: `Translation Corrections${summary ? ` (${summary.translation_corrections})` : ''}` },
    { key: 'placeholders', label: 'Broken Placeholders' },
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '2em 1.5em 4em', fontFamily: 'var(--polyglot-font, system-ui, sans-serif)' }}>
      <Helmet title="Upstream Issues" />

      <div style={{ marginBottom: '1em', fontSize: '0.9em' }}>
        <Link to="/polyglot">Polyglot</Link>
        {' / '}
        <Link to="/polyglot/qc">QC Dashboard</Link>
        {' / '}
        <span>Upstream Issues</span>
      </div>

      <div className="polyglot-page-header">
        <h1>Upstream Issues</h1>
        <p className="subtitle">
          Corrections to report back to the Plone community
        </p>
      </div>

      <div style={{ background: '#f0f8ff', border: '1px solid #b8daff', borderRadius: 4, padding: '1em 1.2em', marginBottom: '1.5em', fontSize: '0.95em', lineHeight: 1.5 }}>
        These are corrections we made to English source text (.pot files) and community translations (.po files)
        for upstream Plone platforms. They should be reported as patches or pull requests to the relevant repositories.
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #ddd', marginBottom: '1.5em' }}>
        {tabs.map((tab) => (
          <button key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '0.8em 1.5em', border: 'none', background: 'none', fontSize: '1em',
              cursor: 'pointer', borderBottom: `3px solid ${activeTab === tab.key ? '#0077b6' : 'transparent'}`,
              marginBottom: -2, color: activeTab === tab.key ? '#0077b6' : '#555',
              fontWeight: activeTab === tab.key ? 600 : 400, fontFamily: 'inherit',
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'english' && <EnglishTab summary={summary} />}
      {activeTab === 'translations' && <TranslationsTab />}
      {activeTab === 'placeholders' && <PlaceholdersTab />}
    </div>
  );
};

export default UpstreamIssues;
